import { Logger } from '_common/services';
import { Command } from '../../Command';

export class CreateDocumentStyleCommand extends Command {
  private options: Editor.Edition.StylesCommandOptions;
  private baseStyleData: string | Partial<Editor.Data.Structure.DocumentStyleData>;

  private createdStyleData?: Editor.Data.Structure.DocumentStyleData;

  constructor(
    context: Editor.Edition.Context,
    baseStyleData: string | Partial<Editor.Data.Structure.DocumentStyleData>,
    options: Editor.Edition.StylesCommandOptions = {},
  ) {
    super(context, {
      createPatch: options.createPatch,
    });

    this.baseStyleData = baseStyleData;
    this.options = {
      applySelection: false,
      createPatch: true,
      untrackedActionWarning: true,
      ...options,
    };
  }

  getCreatedDocumentStyleData() {
    return this.createdStyleData;
  }

  private async createFromOtherStyle(ctx: Editor.Edition.ActionContext, baseStyleId: string) {
    const stylesController = this.context.DataManager?.styles;
    if (!stylesController || !this.context.stylesManager) {
      return false;
    }

    const docStyleStatus =
      this.context.stylesManager.documentStyles.getDocumentStyleStatusFromRange(ctx.range);

    if (!docStyleStatus) {
      throw new Error('Unable to get style status from selection!');
    }

    const baseStyle = stylesController.documentStyles.style(baseStyleId);

    if (!baseStyle) {
      throw new Error('Base style not found!');
    }

    const { name, id } = stylesController.documentStyles.getNameAndIdForStyle(baseStyle);

    if (!id) {
      throw new Error('No id found for new style!');
    }

    if (stylesController.documentStyles.style(id)) {
      throw new Error(`New style Id already exists: ${id}`);
    }

    return this.createWithData({
      id,
      n: name,
      b: false,
      e: baseStyleId,
      p: docStyleStatus.p,
    });
  }

  private async createWithData(styleData: Partial<Editor.Data.Structure.DocumentStyleData>) {
    const stylesController = this.context.DataManager?.styles;
    if (!stylesController || !this.context.stylesManager) {
      return false;
    }

    if (!styleData.id) {
      styleData.id = stylesController.generateStyleId();
    }

    if (!styleData.b) {
      styleData.b = false;
    }

    if (!styleData.e) {
      styleData.e = 'p';
    }

    if (!styleData.p) {
      styleData.p = {};
    }

    if (stylesController.isDocumentStyleData(styleData)) {
      this.createdStyleData = styleData;
      return stylesController.documentStyles.addNewStyle(
        this.createdStyleData.id,
        this.createdStyleData,
      );
    }

    return false;
  }

  protected async handleExec(...args: any[]): Promise<void> {
    if (this.context.debug) {
      Logger.trace('CreateDocumentStyleCommand exec', this);
    }

    if (this.options.untrackedActionWarning && !this.askUserAboutThis()) {
      return;
    }

    this.buildActionContext();

    if (!this.actionContext) {
      throw new Error('Context is not defined');
    }

    if (typeof this.baseStyleData === 'string') {
      if (!(await this.createFromOtherStyle(this.actionContext, this.baseStyleData))) {
        return;
      }
    } else {
      if (!(await this.createWithData(this.baseStyleData))) {
        return;
      }
    }

    //trigger seelction check
    this.checkSelectionStatus();

    if (this.execOptions.createPatch) {
      this.createPatch();
    }
  }
}
